import React, { useEffect } from 'react'
import cls from 'classnames'
import styled from 'styled-components'

import { PLAN_TYPES } from 'containers/App/constants'

import CKEditor from 'components/elements/CKEditor'
import GroupSettingsBanner from 'components/elements/GroupSettingsBanner'
import LargeBanner from 'components/elements/LargeBanner'
import Button from 'components/lib/DesignSystem/Button'
import Checkbox from 'components/lib/DesignSystem/Checkbox'
import { Refresh } from 'components/lib/DesignSystem/Icons'
import Select from 'components/lib/DesignSystem/Select'
import Popup from 'components/lib/Popup'

function MailboxProtectionSettings({
  data,
  className,
  licenses,
  permissions,

  enabledLanguages,
  selectedBannersLanguage,

  changeField,
  changeBannersLanguage,
  updMailboxProtection,

  isGroupSettings,
  groupSettingsNameByPartner,

  isStaticSalesDemo,
}) {
  const defaultLanguage = enabledLanguages.find((elem) => elem.default)?.id
  useEffect(() => {
    if (!selectedBannersLanguage) {
      changeBannersLanguage(defaultLanguage)
    }
  }, [])
  const handleBannerChange = (name, value) =>
    changeField(name, value, selectedBannersLanguage || defaultLanguage)
  const handleLanguageChange = (obj) => changeBannersLanguage(obj)

  const languageOptions = enabledLanguages?.map((l) => ({
    value: l.id,
    label: l.name,
  }))

  const bannersData = Object.keys(data).filter((bannerName) => bannerName.includes('sensor'))

  const planIsStarter = licenses.plan === PLAN_TYPES.STARTER

  return (
    <div className={className}>
      {planIsStarter && (
        <LargeBanner
          text="Impersonation Protection alerts end-users in real-time of potential attackers"
          subText="Aid your colleagues to identify potential impersonation attacks and to report these incidents"
          buttonText="Learn more"
          type="upgrade"
          id="settings-impersonationProtection-upgrade"
        />
      )}

      <GroupSettingsBanner isGroupSettingsFlow={isGroupSettings} />

      <div
        className={cls({
          'disabled-section': planIsStarter,
          disabled_for_edit: !isGroupSettings && groupSettingsNameByPartner,
        })}
      >
        <span className="heading">Impersonation Protection Display</span>
        <div className="banners-title-container">
          <Checkbox
            className="mb"
            id="enableIronsightsBanners"
            checked={!planIsStarter && data.enableIronsightsBanners}
            label="Display Impersonation Protection"
            onChange={(e) => changeField('enableIronsightsBanners', e.target.checked)}
          />
          <Checkbox
            id="enableFirstTimeSender"
            checked={!planIsStarter && data.enableFirstTimeSender}
            label="Display first time sender banner"
            onChange={(e) => changeField('enableFirstTimeSender', e.target.checked)}
          />
          <div className="select-container">
            <Select
              showSearch={false}
              className="is-select"
              placeholder="Select default language"
              value={selectedBannersLanguage}
              options={languageOptions}
              onSelect={handleLanguageChange}
              id="impersonationProtectionBannersLanguage"
            />
          </div>
          {bannersData.map((bannerName) => {
            const banner = data[bannerName]
            const firstDefaultKey = Object.keys(banner['data-default'])[0]
            const localizedBannerText =
              banner.texts[selectedBannersLanguage] ||
              banner['data-default'][selectedBannersLanguage] ||
              banner['data-default'][firstDefaultKey]
            const dataDefaultByLocale =
              banner['data-default'][selectedBannersLanguage] ||
              banner['data-default'][firstDefaultKey]
            return (
              <div
                key={`${bannerName}-${selectedBannersLanguage}`}
                id={`banner-${bannerName}`}
                className="banner-text-container"
              >
                <span className="field-name smr">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {banner.label}
                    {banner.label === 'First Time Sender' && (
                      <Popup
                        position="right"
                        ContentComponent={
                          <>
                            This banner will appear when the recipients get an email from the sender
                            that were in correspondence with them
                            <ul>
                              <li>
                                <b>Google Workspace: </b>
                                External and internal email addresses will be flagged
                              </li>
                              <li>
                                <b>Office 365: </b>
                                Internal senders will not be flagged with the first-time sender
                                banner Reference
                              </li>
                            </ul>
                          </>
                        }
                      />
                    )}
                  </div>
                </span>
                {localizedBannerText !== dataDefaultByLocale && (
                  <div className="default-container">
                    <Button
                      text="Default"
                      styleType="tertiary"
                      dsIconStyles={{ fill: '#0152CC' }}
                      LeftIconDS={Refresh}
                      onClick={() => handleBannerChange(bannerName, dataDefaultByLocale)}
                      id={bannerName && `${bannerName}_default_button`}
                    />
                  </div>
                )}
                <CKEditor
                  id={bannerName}
                  value={localizedBannerText}
                  onChange={(val) => handleBannerChange(bannerName, val)}
                  disabled={planIsStarter || isStaticSalesDemo}
                  configuration="impersonation"
                  activeUpdateMode
                />
              </div>
            )
          })}
          <br />
          <br />
        </div>
        {!planIsStarter && !isGroupSettings && (
          <div className="submit-container">
            <Button
              text="Save Banners Settings"
              styleType="secondary"
              disabled={!permissions.admin || groupSettingsNameByPartner || isStaticSalesDemo}
              onClick={updMailboxProtection}
              id="impersonationProtectionBannersSaveButton"
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default styled(MailboxProtectionSettings)`
  padding: 50px 25px;
  .heading {
    color: ${(props) => props.theme.primaryColor};
    font-weight: bold;
    font-size: 18px;
    margin: 10px 0;
    display: block;
  }
  .banners-title-container {
    position: relative;
    .select-container {
      width: 150px;
      position: absolute;
      top: 0;
      right: 0;
      .is-antd-select-container {
        width: 100%;
        .ant-select {
          width: 100%;
        }
      }
    }
  }
  .banner-text-container {
    width: 90%;
    .field-name {
      color: ${(props) => props.theme.primaryColor};
      font-weight: bold;
      font-size: 15px;
      display: inline-block;
      line-height: 4;
    }
    .cke_bottom {
      display: none;
    }
    .cke_contents {
      height: 100px !important;
    }
    .default-container {
      position: relative;
      top: 18px;
      display: inline-block;
      float: right;
    }
  }
  .submit-container {
    position: absolute;
    right: 30px;
    bottom: 30px;
  }
`
